import { TREXPERT_PERSON_HEADER } from "@kmu-trexpert/shared";
import { type TransportHandler } from "@quatico/magellan-shared";

import { isIamCdsEnabled } from "./iam";
import { readIamSessionToken } from "./iam/session-token";

/** localStorage key for the signed-in dev persona (decision D3: persisted session). */
export const PERSONA_STORAGE_KEY = "trexpert.persona";

export const getPersona = (): string | null =>
    typeof localStorage !== "undefined" ? localStorage.getItem(PERSONA_STORAGE_KEY) : null;

export const setPersona = (personId: string): void => localStorage.setItem(PERSONA_STORAGE_KEY, personId);

export const clearPersona = (): void => localStorage.removeItem(PERSONA_STORAGE_KEY);

/**
 * Magellan fetch transport for the trexpert server. Identity travels per mode
 * (CLAUDE.md rule 6): with IAM-CDS delegation on, the real IAM-CDS bearer token
 * goes in `Authorization` (the server validates it against iam-cds's JWKS); with
 * it off, the dev persona id goes in `x-trexpert-person` (decision D2). The two
 * are mutually exclusive so the server's resolver sees exactly one identity
 * source. A guard denial comes back as a Magellan error envelope (HTTP 200) that
 * magellan-client rejects with the «Kein Zugriff» message — NOT a transport
 * failure here.
 */
export const trexpertAuthFetchFn: TransportHandler = async func => {
    const { name, payload, namespace, endpoint } = func;
    if (!name) {
        throw new Error('Cannot invoke a remote function without a "name".');
    }
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (isIamCdsEnabled()) {
        const token = readIamSessionToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    } else {
        const persona = getPersona();
        if (persona) {
            headers[TREXPERT_PERSON_HEADER] = persona;
        }
    }
    const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ name, data: payload, namespace }),
    });
    if (response.status >= 300) {
        return Promise.reject(
            new Error(`Remote function "${name}" failed: ${response.status} ${response.statusText}`)
        );
    }
    return response.text();
};
